import { useState, useEffect, useMemo } from "react";
import {
  FileText,
  Upload,
  Plus,
  Search,
  File,
  Trash2,
  ExternalLink,
  Download,
  FileSignature,
  Send,
  PenTool,
  RefreshCw,
} from "lucide-react";
import { colors, fonts, DOCUMENT_STATUSES } from "../styles/theme";
import { Button, Spinner, PageHeader } from "../components/ui";
import {
  fetchDocuments,
  deleteDoc,
  fetchAllNdas,
  fmtDate,
  sendDocument,
  sendDocumentForSignature,
  refreshDocumentStatus,
} from "../services/api";
import FileUploadModal from "../components/FileUploadModal";
import SignatureFieldEditor from "../components/SignatureFieldEditor/SignatureFieldEditor";
import { useNavigate } from "react-router-dom";

const TABS = [
  { id: "all", label: "All" },
  { id: "document", label: "Documents" },
  { id: "file", label: "Files" },
  { id: "nda", label: "NDAs" },
];

function StatusBadge({ status }) {
  const cfg = DOCUMENT_STATUSES[status] || DOCUMENT_STATUSES.draft;
  return (
    <span
      style={{
        padding: "3px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  );
}

export default function Documents() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      if (tab === "nda") {
        const result = await fetchAllNdas({ status: statusFilter, limit: 100 });
        setDocuments(result.ndas || []);
        setTotal(result.total || 0);
      } else {
        const result = await fetchDocuments({
          type: tab === "all" ? "" : tab,
          status: statusFilter,
          search,
          limit: 100,
        });
        setDocuments(result.documents || []);
        setTotal(result.total || 0);
      }
    } catch (err) {
      console.error("Failed to load documents:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab, statusFilter, search]);

  const stats = useMemo(() => {
    const docs = documents.filter((d) => d.type === "document");
    const files = documents.filter((d) => d.type === "file");
    return {
      total: documents.length,
      drafts: docs.filter((d) => d.status === "draft").length,
      review: docs.filter((d) => d.status === "review").length,
      sent: docs.filter((d) => d.status === "sent").length,
      files: files.length,
    };
  }, [documents]);

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title || doc.filename}"?`)) return;
    try {
      await deleteDoc(doc.id);
      load();
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    }
  };

  const handleSendFile = async (doc) => {
    if (!doc.contactEmail) {
      alert("This file has no linked contact email. Please link a contact first.");
      return;
    }
    if (!window.confirm(`Send "${doc.title || doc.filename}" as an email attachment to ${doc.contactEmail}?`)) return;
    try {
      await sendDocument(doc.id, { recipientEmail: doc.contactEmail });
      load();
    } catch (err) {
      alert(`Send failed: ${err.message}`);
    }
  };

  // E-signature field editor state
  const [fieldEditorDoc, setFieldEditorDoc] = useState(null);

  const handleSignFile = (doc) => {
    if (!doc.contactEmail) {
      alert("This file has no linked contact email. Please link a contact first.");
      return;
    }
    setFieldEditorDoc(doc);
  };

  const handleSignWithFields = async ({ fields, counterSign, counterSignerEmail, counterSignerName }) => {
    await sendDocumentForSignature(fieldEditorDoc.id, {
      recipientEmail: fieldEditorDoc.contactEmail,
      fields,
      counterSign,
      counterSignerEmail,
      counterSignerName,
    });
    setFieldEditorDoc(null);
    load();
  };

  const statCards = [
    { label: "Total", value: stats.total, color: colors.accent },
    { label: "Drafts", value: stats.drafts, color: "#94a3b8" },
    { label: "In Review", value: stats.review, color: "#f59e0b" },
    { label: "Sent", value: stats.sent, color: "#10b981" },
    { label: "Files", value: stats.files, color: "#6366f1" },
  ];

  return (
    <div>
      <PageHeader
        title="Documents"
        subtitle="Create, manage, and share client documents and files."
      >
        <Button variant="default" onClick={() => setShowUploadModal(true)}>
          <Upload size={14} /> Upload File
        </Button>
        <Button variant="primary" onClick={() => navigate("/doc-editor")}>
          <Plus size={14} /> New Document
        </Button>
      </PageHeader>

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 24 }}>
        {statCards.map((s) => (
          <div
            key={s.label}
            style={{
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              padding: "16px 20px",
            }}
          >
            <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, fontFamily: fonts.heading, color: s.color }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Tabs + Filters */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 4, background: colors.bgHover, borderRadius: 6, padding: 3 }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: "6px 14px",
                borderRadius: 4,
                border: "none",
                background: tab === t.id ? colors.bgCard : "transparent",
                color: tab === t.id ? colors.text : colors.textSecondary,
                fontSize: 13,
                fontWeight: 600,
                fontFamily: fonts.body,
                cursor: "pointer",
                boxShadow: tab === t.id ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: "6px 10px",
            fontSize: 13,
            fontFamily: fonts.body,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            background: colors.bgCard,
            color: colors.text,
            outline: "none",
            cursor: "pointer",
          }}
        >
          <option value="">All statuses</option>
          {Object.entries(DOCUMENT_STATUSES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>

        <div style={{ position: "relative", flex: 1, maxWidth: 300 }}>
          <Search size={14} color={colors.textSecondary} style={{ position: "absolute", left: 10, top: 9 }} />
          <input
            type="text"
            placeholder="Search documents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "7px 12px 7px 32px",
              fontSize: 13,
              fontFamily: fonts.body,
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: fonts.body }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.border}`, background: colors.bg }}>
              <th style={thStyle}>Title</th>
              {tab === "nda" ? (
                <>
                  <th style={thStyle}>Contact</th>
                  <th style={thStyle}>Company</th>
                </>
              ) : (
                <>
                  <th style={thStyle}>Company</th>
                  <th style={thStyle}>Type</th>
                </>
              )}
              <th style={thStyle}>Status</th>
              {tab !== "nda" && <th style={thStyle}>Tags</th>}
              <th style={thStyle}>{tab === "nda" ? "Sent" : "Date"}</th>
              <th style={{ ...thStyle, width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={tab === "nda" ? 6 : 7} style={{ padding: 40, textAlign: "center", color: colors.textSecondary }}>
                  <Spinner size={20} />
                </td>
              </tr>
            ) : documents.length === 0 ? (
              <tr>
                <td colSpan={tab === "nda" ? 6 : 7} style={{ padding: 40, textAlign: "center", color: colors.textSecondary }}>
                  {tab === "nda" ? "No NDAs sent yet." : `No documents found${search ? ` matching "${search}"` : ""}.`}
                </td>
              </tr>
            ) : (
              documents.map((doc) => (
                <tr
                  key={doc.id}
                  style={{ borderBottom: `1px solid ${colors.borderLight}`, cursor: doc.type === "document" ? "pointer" : "default" }}
                  onClick={() => doc.type === "document" && navigate(`/doc-editor?id=${doc.id}`)}
                >
                  <td style={tdStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {doc.type === "nda" ? (
                        <FileSignature size={14} color="#f59e0b" />
                      ) : doc.type === "file" ? (
                        <File size={14} color="#6366f1" />
                      ) : (
                        <FileText size={14} color={colors.accent} />
                      )}
                      <span style={{ fontWeight: 600 }}>{doc.title || doc.filename}</span>
                    </div>
                  </td>
                  {tab === "nda" ? (
                    <>
                      <td style={tdStyle}>
                        <div>
                          <div style={{ fontWeight: 500 }}>{doc.contactName || "—"}</div>
                          <div style={{ fontSize: 11, color: colors.textSecondary }}>{doc.contactEmail || ""}</div>
                        </div>
                      </td>
                      <td style={tdStyle}>{doc.companyName || "—"}</td>
                    </>
                  ) : (
                    <>
                      <td style={tdStyle}>{doc.companyName || "—"}</td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: 11, textTransform: "capitalize" }}>{doc.type}</span>
                      </td>
                    </>
                  )}
                  <td style={tdStyle}><StatusBadge status={doc.status} /></td>
                  {tab !== "nda" && (
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {(doc.tags || []).slice(0, 3).map((t) => (
                          <span
                            key={t}
                            style={{
                              padding: "2px 6px",
                              borderRadius: 4,
                              fontSize: 10,
                              background: colors.accentDim,
                              color: colors.textSecondary,
                            }}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </td>
                  )}
                  <td style={tdStyle}>{fmtDate(tab === "nda" ? doc.sentAt : doc.updatedAt)}</td>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                      {doc.signedPdfUrl && (
                        <a
                          href={doc.signedPdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View signed PDF"
                          style={{ color: "#10b981" }}
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                      {doc.type === "file" && doc.sasUrl && (
                        <a
                          href={doc.sasUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Download"
                          style={{ color: colors.info }}
                        >
                          <Download size={14} />
                        </a>
                      )}
                      {doc.type === "file" && doc.status !== "sent" && doc.status !== "pending_signature" && doc.status !== "signed" && (
                        <button
                          onClick={() => handleSendFile(doc)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: colors.accent, padding: 0 }}
                          title="Send as email attachment"
                        >
                          <Send size={14} />
                        </button>
                      )}
                      {doc.type === "file" && doc.status !== "pending_signature" && doc.status !== "signed" && (
                        <button
                          onClick={() => handleSignFile(doc)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#f59e0b", padding: 0 }}
                          title="Send for e-signature"
                        >
                          <PenTool size={14} />
                        </button>
                      )}
                      {doc.status === "pending_signature" && (
                        <button
                          onClick={async () => {
                            try {
                              await refreshDocumentStatus(doc.id);
                              load();
                            } catch (err) {
                              alert(`Refresh failed: ${err.message}`);
                            }
                          }}
                          style={{ background: "none", border: "none", cursor: "pointer", color: colors.accent, padding: 0 }}
                          title="Refresh signature status"
                        >
                          <RefreshCw size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(doc)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: colors.danger, padding: 0 }}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showUploadModal && (
        <FileUploadModal
          onClose={() => setShowUploadModal(false)}
          onUploaded={load}
        />
      )}

      {fieldEditorDoc && (
        <SignatureFieldEditor
          pdfUrl={fieldEditorDoc.sasUrl}
          documentName={fieldEditorDoc.title || fieldEditorDoc.filename}
          recipientEmail={fieldEditorDoc.contactEmail}
          recipientName={fieldEditorDoc.contactName}
          onClose={() => setFieldEditorDoc(null)}
          onSend={handleSignWithFields}
        />
      )}
    </div>
  );
}

const thStyle = {
  padding: "10px 14px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#9ca3b4",
};

const tdStyle = {
  padding: "12px 14px",
};
